import React from 'react';
import { useLocation } from 'react-router-dom';
import { IconButton, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import BarChartIcon from '@material-ui/icons/BarChart';
import { Link } from '@backstage/core-components';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { useAsync } from 'react-use';
import { useAnalyticsAccessConfig } from '../../hooks/useAnalyticsAccessConfig';

const useStyles = makeStyles((theme) => ({
  iconButton: {
    position: 'fixed',
    top: theme.spacing(2),
    right: theme.spacing(2),
    zIndex: theme.zIndex.speedDial,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
}));

/**
 * Icon button that links to the analytics detail page for the current page
 * Hidden on analytics pages themselves
 * Only visible to users in the backstage_operators group
 */
export const PageAnalyticsFab = () => {
  const classes = useStyles();
  const location = useLocation();
  const identityApi = useApi(identityApiRef);
  
  // Get allowed groups from config
  const allowedGroups = useAnalyticsAccessConfig();
  
  // Get user's group memberships
  const { value: userIdentity, loading } = useAsync(async () => {
    const identity = await identityApi.getBackstageIdentity();
    return identity.ownershipEntityRefs;
  }, [identityApi]);

  const userOwnership = userIdentity || [];

  // Don't show on analytics pages
  if (location.pathname.startsWith('/analytics')) {
    return null;
  }

  // If no groups are configured, hide the icon
  if (allowedGroups.length === 0) {
    return null;
  }

  // Wait for user identity to load
  if (loading) {
    return null;
  }

  // Check if user is in any of the allowed groups
  const hasAccess = allowedGroups.some(group => userOwnership.includes(group));
  
  if (!hasAccess) {
    return null;
  }

  const analyticsUrl = `/analytics/page/${encodeURIComponent(location.pathname)}`;

  return (
    <Tooltip title="View Analytics for this Page" placement="left">
      <IconButton
        className={classes.iconButton}
        component={Link}
        to={analyticsUrl}
        aria-label="view page analytics"
        size="small"
      >
        <BarChartIcon />
      </IconButton>
    </Tooltip>
  );
};
